import React from "react";
import { Calendar, ArrowLeftRight, Activity, RotateCcw } from "lucide-react";

// Standalone OptionStrat-style chart-footer scenario bar for /forge.
// Three horizontal controls: time-forward, spot-shock %, IV-shock pts.
// State is owned by ForgePage and passed in/out via setters.
const ChartScenarioBar = ({
  timeFrac = 0, setTimeFrac,
  shockPct = 0, setShockPct,
  ivShockVp = 0, setIvShockVp,
  vega = 0,
  expiryDays = 30,
  spot,
  netPremium,
}) => {
  const ivAvailable = Number.isFinite(vega) && Math.abs(vega) > 0.0001;
  const dirty = (timeFrac && timeFrac !== 0) || (shockPct && shockPct !== 0) || (ivShockVp && ivShockVp !== 0);
  const currentDays = Math.round((timeFrac || 0) * (expiryDays || 0) * 10) / 10;
  const atExpiry = (timeFrac || 0) >= 0.999;
  const shockedSpot = Number.isFinite(spot) ? spot * (1 + (shockPct || 0)) : null;

  const dateLabel = (() => {
    if (currentDays <= 0.1) return "Today (T+0)";
    if (atExpiry) return `@ Expiry (T+${expiryDays}d)`;
    const d = new Date();
    d.setDate(d.getDate() + Math.round(currentDays));
    const opts = { weekday: "short", month: "short", day: "numeric" };
    return `${d.toLocaleDateString("en-US", opts)} (T+${Math.round(currentDays)}d)`;
  })();

  return (
    <div
      className="bg-[#0E1A30] border border-white/10 rounded-xl px-4 py-3 grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-3"
      data-testid="forge-chart-scenario-bar"
    >
      {/* DATE */}
      <div className="flex flex-col gap-1.5 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="text-[10px] uppercase tracking-[0.22em] text-white/45 inline-flex items-center gap-1.5">
            <Calendar className="w-3 h-3 text-[#60A5FA]" /> Date
          </span>
          <span className="text-[10.5px] num text-white/85 truncate" data-testid="forge-scenario-date-label">
            {dateLabel}
          </span>
        </div>
        <input
          type="range"
          min={0}
          max={1}
          step={0.01}
          value={timeFrac || 0}
          onChange={(e) => setTimeFrac?.(Number(e.target.value))}
          className="w-full h-1.5 accent-[#3B82F6] cursor-pointer"
          data-testid="forge-scenario-time-slider"
          aria-label="Time forward (today → expiry)"
        />
      </div>

      {/* RANGE */}
      <div className="flex flex-col gap-1.5 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="text-[10px] uppercase tracking-[0.22em] text-white/45 inline-flex items-center gap-1.5">
            <ArrowLeftRight className="w-3 h-3 text-[#60A5FA]" /> Range
          </span>
          <span className="text-[10.5px] num text-white/85" data-testid="forge-scenario-shock-label">
            {(shockPct || 0) >= 0 ? "+" : ""}{((shockPct || 0) * 100).toFixed(1)}%
            {shockedSpot != null && Math.abs(shockPct || 0) > 0.001 && (
              <span className="text-white/45 ml-1.5">&rarr; ${shockedSpot.toFixed(2)}</span>
            )}
          </span>
        </div>
        <input
          type="range"
          min={-0.20}
          max={0.20}
          step={0.005}
          value={shockPct || 0}
          onChange={(e) => setShockPct?.(Number(e.target.value))}
          className="w-full h-1.5 accent-[#3B82F6] cursor-pointer"
          data-testid="forge-scenario-shock-slider"
          aria-label="Spot shock ±%"
        />
      </div>

      {/* IMPLIED VOLATILITY */}
      <div className="flex flex-col gap-1.5 min-w-0">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-[0.22em] text-white/45 inline-flex items-center gap-1.5">
            <Activity className="w-3 h-3 text-[#60A5FA]" /> Implied Vol
            {!ivAvailable && (
              <span className="text-white/30 normal-case tracking-normal ml-1">(no vega)</span>
            )}
          </span>
          <div className="flex items-center gap-1">
            <span className="text-[10.5px] num text-white/85 mr-1">
              {(ivShockVp || 0) >= 0 ? "+" : ""}{Number(ivShockVp || 0).toFixed(1)}pt
            </span>
            {[0, 5, 10].map((p, idx) => {
              const mult = idx + 1;
              const active = Math.round(Math.abs(ivShockVp || 0)) === p;
              return (
                <button
                  key={`iv-mult-${mult}`}
                  type="button"
                  onClick={() => setIvShockVp?.(p)}
                  data-testid={`forge-scenario-iv-x${mult}`}
                  className={`text-[9.5px] px-1.5 py-0.5 rounded font-medium transition ${
                    active
                      ? "bg-[#3B82F6] text-white"
                      : "border border-white/10 text-white/55 hover:text-white hover:border-white/30"
                  }`}
                >
                  ×{mult}
                </button>
              );
            })}
          </div>
        </div>
        <input
          type="range"
          min={-20}
          max={20}
          step={0.5}
          value={ivShockVp || 0}
          onChange={(e) => setIvShockVp?.(Number(e.target.value))}
          disabled={!ivAvailable}
          className="w-full h-1.5 accent-[#3B82F6] cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="forge-scenario-iv-slider"
          aria-label="IV shock vol-points"
        />
      </div>

      {dirty && (
        <div className="md:col-span-3 -mt-1 flex justify-end">
          <button
            type="button"
            onClick={() => { setTimeFrac?.(0); setShockPct?.(0); setIvShockVp?.(0); }}
            data-testid="forge-scenario-reset"
            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded border border-white/15 text-white/55 hover:text-white hover:border-white/30 transition"
          >
            <RotateCcw className="w-3 h-3" /> Reset scenario
          </button>
        </div>
      )}
    </div>
  );
};

export default ChartScenarioBar;
